<template>
    <div class="container-fluid">
        <div
            class="row"
            v-if="loading"
        >
            <div class="col">
                <p>Loading...</p>
            </div>
        </div>
        <div v-if="!loading">
            <form
                action=""
                @submit.prevent="save"
            >
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-12">
                                <label>Event Type:</label>
                                <select
                                    v-model="form.type"
                                    class="form-control"
                                    :disabled="!canSave"
                                >
                                    <option value="Annual Leave">
                                        Annual Leave
                                    </option>
                                    <option value="Sickness">
                                        Sickness
                                    </option>
                                </select>
                                <Error
                                    :errors="errors"
                                    name="type"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12 col-md-6">
                                <label>Start Date:</label>
                                <date-picker
                                    v-model="form.start_at"
                                    :disabled="!canSave"
                                    :options="startDatePickerOptions"
                                />
                                <Error
                                    :errors="errors"
                                    name="start_at"
                                />
                            </div>
                            <div class="col-12 col-md-6">
                                <label>End Date:</label>
                                <date-picker
                                    v-model="form.end_at"
                                    :options="endDatePickerOptions"
                                    :disabled="!canSave"
                                />
                                <Error
                                    :errors="errors"
                                    name="end_at"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <label>Notes</label>
                                <textarea
                                    v-model="form.notes"
                                    placeholder="Enter additional notes here..."
                                    rows="4"
                                    class="form-control"
                                    :disabled="!canSave"
                                />
                                <Error
                                    :errors="errors"
                                    name="notes"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <label>Status:</label>
                                <select
                                    v-model="form.status"
                                    class="form-control"
                                    :disabled="true"
                                >
                                    <option>
                                        Pending
                                    </option>
                                    <option>
                                        Approved
                                    </option>
                                    <option>
                                        Declined
                                    </option>
                                </select>
                                <Error
                                    :errors="errors"
                                    name="status"
                                />
                            </div>
                        </div>
                        <div
                            v-if="form.reviewer"
                            class="row"
                        >
                            <div class="col-12">
                                <label>Reviewed By:</label>
                                <p>{{ form.reviewer.name }} at {{ form.reviewed_at | datetime }}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div
                            class="row"
                            style="margin-top: 1rem;"
                        >
                            <div class="col-12 flex">
                                <button
                                    class="btn btn-primary"
                                    :disabled="!canSave"
                                >
                                    Save
                                </button>
                                <button
                                    class="btn btn-danger"
                                    v-if="form.id"
                                    type="button"
                                    @click="handleDeleteClick"
                                >
                                    Delete
                                </button>
                                <router-link
                                    to="../calendar"
                                    class="btn btn-link"
                                >
                                    Back
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import CalendarEventsService from "../../services/CalendarEventsService";
import UsesErrors from "../../mixins/UsesErrors";
import UsesInfoMessage from "../../mixins/UsesInfoMessage";
import DatePicker from "../DateTimePickerComponent.vue";
import Error from "../ErrorComponent.vue";
import {normalizeDateOnlyRange} from "../../include/Calendar";

const initialForm = {
    type: "Annual Leave",
    start_at: window.moment().startOf("day"),
    end_at: window.moment().endOf("day"),
    status: "Pending",
};

export default {
    components: {
        DatePicker,
        Error,
    },
    mixins: [UsesErrors, UsesInfoMessage],
    data() {
        return {
            form: { ...initialForm },
            loading: false,
        };
    },
    computed: {
        isNew() {
            return this.form.id === undefined;
        },
        id() {
            return this.$route.params.id;
        },
        canSave() {
            return this.form.status === "Pending";
        },
        startDatePickerOptions() {
            return {format: "L"};
        },
        endDatePickerOptions() {
            const options = {format: "L"};
            if (this.form.start_at && window.moment.isMoment(this.form.start_at)) {
                options.minDate = this.form.start_at.clone().startOf("day");
            }
            return options;
        },
    },
    watch: {
        id: {
            immediate: true,
            handler() {
                this.reset();
                if (!this.id) {
                    return;
                }
                this.loadEvent();
            },
        },
        "form.start_at"(start) {
            if (
                window.moment.isMoment(start) &&
                window.moment.isMoment(this.form.end_at) &&
                start.isAfter(this.form.end_at, "day")
            ) {
                this.form.end_at = start.clone().endOf("day");
            }
        },
    },
    methods: {
        loadEvent() {
            this.loading = true;
            CalendarEventsService.fetchEvent(this.id)
                .then(({ data }) => {
                    this.form = {
                        ...data,
                        start_at: window.moment(data.start_at),
                        end_at: window.moment(data.end_at),
                    };
                    this.loading = false;
                })
                .catch((err) => {
                    console.error(err);
                    this.$router.back();
                });
        },
        reset() {
            this.form = { ...initialForm };
            this.loading = false;
            this.clearErrors();
        },
        save() {
            this.clearErrors();
            this.setSaving(true);
            const formData = {
                ...this.form,
                ...normalizeDateOnlyRange(this.form.start_at, this.form.end_at),
                user_id: window.AuthService.user.id,
            };
            CalendarEventsService.saveEvent(formData)
                .then(({ data }) => {
                    if (!this.id) {
                        this.$router.push(`./${data.id}`);
                        return;
                    }
                    this.form = data;
                    this.setSaving(false);
                })
                .catch((err) => {
                    this.captureRequestErrors(err);
                    this.saving = false;
                    this.setSaving(false);
                });
        },
        handleDeleteClick() {
            const confirm = window.confirm(
                "Are you sure you want to delete this event?"
            );
            if (!confirm) {
                return;
            }
            this.loading = true;
            CalendarEventsService.deleteEvent(this.form)
                .then(() => {
                    this.$router.replace("../calendar");
                })
                .catch((err) => {
                    console.error(err);
                    window.alert("Error: Failed to delete event");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/engineer/CalendarForm.vue